/**
 * The bird — built from 4 primitives per spec §3:
 *   - circle (body)
 *   - circle (head)
 *   - small triangle (beak)
 *   - two circles (eyes) + two inner circles (pupils)
 *
 * Coordinate space: 72×72 viewBox.
 * Head diameter ≈ 55% of total, body ≈ 45%, eye ≈ 40% of head width.
 *
 * Every named element has a stable id so CSS/JS can target it.
 */

export const BIRD_VIEWBOX = '0 0 72 72';

export const BIRD_SVG_BODY = `
  <!-- Tail: 3 short feathers tucked behind -->
  <g class="perch-b-tail">
    <path class="perch-b-tailf" d="M11 46 Q6 48 8 52 Q11 50 14 49 Z" />
    <path class="perch-b-tailf" d="M10 49 Q5 52 9 55 Q12 53 15 52 Z" />
    <path class="perch-b-tailf" d="M11 52 Q7 56 12 58 Q14 55 16 54 Z" />
  </g>

  <!-- Body: lower round (the belly + chest) -->
  <g class="perch-b-body">
    <circle class="perch-b-belly" cx="36" cy="48" r="18" />
    <!-- Chest (coral) is the lower-front portion -->
    <path class="perch-b-chest"
      d="M22 44 Q36 66 50 44 Q50 58 36 66 Q22 58 22 44 Z" />
    <!-- Wing hint — three short arc strokes -->
    <g class="perch-b-wing">
      <path d="M44 42 Q50 47 49 54" />
      <path d="M46 42 Q52 46 51 52" />
      <path d="M48 42 Q53 45 53 50" />
    </g>
  </g>

  <!-- Head: big round, sits high -->
  <g class="perch-b-head" transform-origin="36px 28px">
    <circle class="perch-b-skull" cx="36" cy="28" r="20" />

    <!-- Eyes (whites + pupils) -->
    <g class="perch-b-eyes">
      <g class="perch-b-eye perch-b-eye-l">
        <circle class="perch-b-eyew" cx="28" cy="24" r="5.5" />
        <circle class="perch-b-pupil" cx="28" cy="24" r="2.8" />
        <circle class="perch-b-catchlight" cx="29" cy="22.8" r="0.9" />
        <!-- Eyelid (covers eye during blink/sleep) -->
        <rect class="perch-b-lid perch-b-lid-l" x="22" y="18" width="12" height="6" />
      </g>
      <g class="perch-b-eye perch-b-eye-r">
        <circle class="perch-b-eyew" cx="44" cy="24" r="5.5" />
        <circle class="perch-b-pupil" cx="44" cy="24" r="2.8" />
        <circle class="perch-b-catchlight" cx="45" cy="22.8" r="0.9" />
        <rect class="perch-b-lid perch-b-lid-r" x="38" y="18" width="12" height="6" />
      </g>
    </g>

    <!-- Beak: small triangle, the only sharp thing -->
    <g class="perch-b-beak" transform-origin="36px 32px">
      <path class="perch-b-beak-upper" d="M33 31 L36 35 L39 31 Z" />
      <path class="perch-b-beak-lower" d="M33.5 31 L36 34.5 L38.5 31 Z" />
    </g>

    <!-- Cheek blush (hover + happy only) -->
    <g class="perch-b-blush">
      <circle cx="22" cy="30" r="3" />
      <circle cx="50" cy="30" r="3" />
    </g>
  </g>

  <!-- Feet (happy + sleeping only) -->
  <g class="perch-b-feet">
    <path d="M32 65 L30 68 M32 65 L32 68 M32 65 L34 68" />
    <path d="M40 65 L38 68 M40 65 L40 68 M40 65 L42 68" />
  </g>

  <!-- Thinking dots — rise above head -->
  <g class="perch-b-think">
    <circle class="perch-b-dot perch-b-dot-1" cx="28" cy="6" r="2" />
    <circle class="perch-b-dot perch-b-dot-2" cx="36" cy="6" r="2" />
    <circle class="perch-b-dot perch-b-dot-3" cx="44" cy="6" r="2" />
  </g>

  <!-- Confused "?" bubble -->
  <g class="perch-b-qmark">
    <rect x="38" y="-2" width="22" height="16" rx="6" />
    <text x="49" y="10" text-anchor="middle" font-family="Inter, sans-serif" font-weight="600" font-size="11">?</text>
  </g>

  <!-- Sleeping "z" -->
  <g class="perch-b-zzz">
    <text x="52" y="6" font-family="Fraunces, Georgia, serif" font-style="italic" font-size="12">z</text>
  </g>

  <!-- Listening halo (rendered below everything) -->
  <circle class="perch-b-halo" cx="36" cy="36" r="20" />

  <!-- Thinking dusk glow -->
  <ellipse class="perch-b-glow" cx="36" cy="54" rx="28" ry="6" />

  <!-- Speaking ring (emitted from beak) -->
  <circle class="perch-b-ring" cx="36" cy="34" r="6" />

  <!-- Happy confetti feathers -->
  <g class="perch-b-confetti">
    <rect class="perch-b-feather perch-b-feather-1" x="18" y="14" width="4" height="6" rx="1" />
    <rect class="perch-b-feather perch-b-feather-2" x="36" y="10" width="4" height="6" rx="1" />
    <rect class="perch-b-feather perch-b-feather-3" x="54" y="14" width="4" height="6" rx="1" />
  </g>
`;

/** Self-contained standalone SVG — drop-in for email, docs, external use. */
export function birdSvg(options?: { state?: string; size?: number }): string {
  const state = options?.state ?? 'idle';
  const size = options?.size ?? 72;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${BIRD_VIEWBOX}" width="${size}" height="${size}" class="perch-bird" data-state="${state}" role="img" aria-label="Perch the bird">${BIRD_SVG_BODY}</svg>`;
}
